document.documentElement.classList.remove('no-js');

// Delegate so quick-add forms loaded by search and recommendations also work.
(() => {
  const pending = new WeakSet();
  let queue = Promise.resolve();
  let notice;
  let dismissTimer;
  let quantities = new Map();
  let eventTicketCount = 0;
  const added = new Set();
  const formSelector = '.product-card__quick-add, .product-form, .mobile-purchase-bar__form';
  const selection = (form) => {
    const input = form.querySelector('[name="id"]');
    return input?.tagName === 'SELECT' ? input.selectedOptions[0] : input;
  };
  const remaining = (variant) => variant?.hasAttribute('data-stock')
    ? Math.max(0, Number(variant.dataset.stock) - (quantities.get(variant.value) || 0)) : Infinity;
  const syncForm = (form) => {
    const variant = selection(form);
    if (!variant) return;
    const inCart = quantities.get(variant.value) || 0;
    const left = remaining(variant);
    const unavailable = variant.dataset.available === 'false';
    const eventLimitReached = form.hasAttribute('data-event-ticket') && eventTicketCount > 0;
    const eventLimitNotice = form.querySelector('[data-event-limit-notice]');
    if (eventLimitNotice) eventLimitNotice.hidden = !eventLimitReached;
    const quantity = form.querySelector('[name="quantity"]');
    if (quantity && quantity.type !== 'hidden') {
      if (Number.isFinite(left)) quantity.max = String(left);
      else quantity.removeAttribute('max');
      quantity.disabled = left === 0 || unavailable;
      if (left > 0 && Number(quantity.value) > left) quantity.value = left;
    }
    form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach((button) => {
      const label = inCart > 0 || added.has(variant.value) ? 'Added to cart' : unavailable || left === 0 ? 'Unavailable' : 'Add to cart';
      if (button.tagName === 'INPUT') button.value = label;
      else if (button.textContent !== label) button.textContent = label;
      button.disabled = pending.has(form) || unavailable || left === 0 || eventLimitReached;
    });
  };
  const syncForms = () => document.querySelectorAll(formSelector).forEach(syncForm);
  const syncCartLimits = () => {
    const inputs = [...document.querySelectorAll('[data-cart-variant][data-stock]')];
    inputs.forEach((input) => {
      const others = inputs.filter((other) => other !== input && other.dataset.cartVariant === input.dataset.cartVariant)
        .reduce((sum, other) => sum + Math.max(0, Number(other.value) || 0), 0);
      input.max = String(Math.max(0, Number(input.dataset.stock) - others));
    });
  };
  document.addEventListener('input', (event) => {
    if (event.target.matches('[data-cart-variant]')) syncCartLimits();
  });
  const root = () => `${window.Shopify?.routes?.root || '/'}`.replace(/\/?$/, '/');
  const announce = (message) => {
    if (!notice) {
      notice = document.createElement('div');
      notice.setAttribute('role', 'status');
      notice.setAttribute('aria-live', 'polite');
      notice.style.cssText = 'position:fixed;top:16px;right:16px;z-index:10000;max-width:calc(100vw - 32px);padding:16px 20px;background:var(--fcc-surface,#fff);color:var(--fcc-text,#111);border:1px solid var(--fcc-border,#ccc);border-radius:8px;box-shadow:0 4px 20px #0003;';
      document.body.appendChild(notice);
    }
    clearTimeout(dismissTimer);
    notice.hidden = false;
    notice.replaceChildren(document.createTextNode(`${message} `));
    const link = document.createElement('a');
    link.href = `${root()}cart`;
    link.textContent = 'View cart';
    notice.appendChild(link);
    dismissTimer = setTimeout(() => { notice.hidden = true; }, 8000);
  };
  const refreshCount = async () => {
    const response = await fetch(`${root()}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Cart count unavailable');
    const cart = await response.json();
    quantities = new Map();
    eventTicketCount = 0;
    (cart.items || []).forEach((item) => {
      const id = String(item.variant_id);
      quantities.set(id, (quantities.get(id) || 0) + item.quantity);
      if (item.product_type === 'Event ticket') eventTicketCount += item.quantity;
    });
    syncForms();
    document.querySelectorAll('.cart-link').forEach((link) => {
      const count = link.querySelector('.cart-link__count');
      if (count) count.textContent = cart.item_count;
      link.setAttribute('aria-label', `Cart with ${cart.item_count} items`);
    });
  };
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || event.defaultPrevented) return;
    const action = new URL(form.action, window.location.href);
    if (action.origin !== window.location.origin || !/\/cart\/add(?:\.js)?\/?$/.test(action.pathname)) return;
    event.preventDefault();
    if (pending.has(form)) return;
    const isEventTicket = form.hasAttribute('data-event-ticket');
    if (isEventTicket && !form.reportValidity()) return;
    const body = new FormData(form);
    if (isEventTicket) {
      body.set('properties[Attendee name]', String(body.get('properties[Attendee name]') || '').trim());
      body.set('properties[Wizards account email]', String(body.get('properties[Wizards account email]') || '').trim());
    }
    const variant = selection(form);
    const variantId = String(body.get('id'));
    const stock = variant?.getAttribute('data-stock');
    const buttons = [...form.querySelectorAll('button[type="submit"], input[type="submit"]')];
    const disabled = buttons.map((button) => button.disabled);
    pending.add(form);
    form.setAttribute('aria-busy', 'true');
    buttons.forEach((button) => { button.disabled = true; });
    // Serializing keeps the displayed count correct when different cards are added quickly.
    queue = queue.then(async () => {
      try {
        // Re-read the cart before each queued add, including quantities on other lines.
        await refreshCount();
        if (isEventTicket && eventTicketCount > 0) {
          announce(form.dataset.eventLimitMessage);
          return;
        }
        const left = stock === null || stock === undefined ? Infinity : Math.max(0, Number(stock) - (quantities.get(variantId) || 0));
        const requested = Number(body.get('quantity') || 1);
        if (isEventTicket && (requested !== 1 || !body.get('properties[Attendee name]') || !body.get('properties[Wizards account email]') || body.get('properties[Wizards account confirmed]') !== 'Yes')) {
          announce(form.dataset.eventRegistrationMessage);
          return;
        }
        if (!Number.isInteger(requested) || requested < 1 || requested > left) {
          announce(left === 0 ? 'All available stock is already in your cart.' : `Only ${left} more available. Please choose a smaller quantity.`);
          return;
        }
        const response = await fetch(`${root()}cart/add.js`, {
          method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' }, body
        });
        const result = await response.json();
        if (!response.ok) {
          announce(typeof result.description === 'string' ? result.description : 'This item could not be added. Please check availability.');
        } else {
          added.add(variantId);
          announce('Added to cart. Keep shopping!');
        }
        // Shopify can partially add stock even when returning an inventory error.
        await refreshCount().catch(() => {});
      } catch (error) {
        announce('We could not confirm the addition. Check your cart before trying again.');
      } finally {
        pending.delete(form);
        form.removeAttribute('aria-busy');
        buttons.forEach((button, index) => {
          const variant = form.querySelector('select[name="id"]');
          button.disabled = variant ? variant.selectedOptions[0]?.dataset.available === 'false' : disabled[index];
        });
        syncForms();
      }
    });
  });
  document.addEventListener('change', (event) => {
    const form = event.target.closest(formSelector);
    if (form) queueMicrotask(() => syncForm(form));
  });
  const initialize = () => {
    syncCartLimits();
    refreshCount().catch(() => {});
    new MutationObserver((records) => {
      if (records.some((record) => [...record.addedNodes].some((node) => node.nodeType === 1 && (node.matches(formSelector) || node.querySelector(formSelector))))) syncForms();
    }).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  window.addEventListener('pageshow', () => refreshCount().catch(() => {}));
})();

(() => {
  const mobileFilters = document.querySelectorAll('.mobile-filter-shell');
  if (!mobileFilters.length || !window.matchMedia) return;

  const mobileQuery = window.matchMedia('(max-width: 1100px)');
  const syncFilters = () => {
    mobileFilters.forEach((filter) => {
      if (mobileQuery.matches) filter.removeAttribute('open');
      else filter.setAttribute('open', '');
    });
  };

  syncFilters();
  if (mobileQuery.addEventListener) mobileQuery.addEventListener('change', syncFilters);
})();

(() => {
  document.querySelectorAll('[data-product-recommendations]').forEach((section) => {
    const { url, sectionId } = section.dataset;
    if (!url || !sectionId) return;

    fetch(url, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error('Recommendations request failed');
        return response.text();
      })
      .then((markup) => {
        const html = document.createElement('div');
        html.innerHTML = markup;
        const loaded = html.querySelector(`#shopify-section-${sectionId}`);
        if (loaded?.innerHTML.trim()) section.innerHTML = loaded.innerHTML;
      })
      .catch(() => {});
  });
})();

(() => {
  document.querySelectorAll('.site-nav__details').forEach((details) => {
    details.addEventListener('toggle', () => {
      if (!details.open) return;
      document.querySelectorAll('.site-nav__details[open]').forEach((other) => {
        if (other !== details) other.removeAttribute('open');
      });
    });
  });

  document.addEventListener('click', (event) => {
    if (event.target.closest('.site-nav__details')) return;
    document.querySelectorAll('.site-nav__details[open]').forEach((details) => details.removeAttribute('open'));
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('.site-nav__details[open]').forEach((details) => details.removeAttribute('open'));
  });
})();
